import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Check, Info, Loader2, SquarePen, Sparkles } from "lucide-react";
import { projectsApi } from "@/services/projectsApi";
import { useProject } from "@/hooks/useProjects";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

type ProjectTemplateData = Awaited<ReturnType<typeof projectsApi.getProjectTemplate>>;

const CONTENT_INTELLIGENCE_API_URL = getEnv("VITE_CONTENT_INTELLIGENCE_API_URL");

interface DescriptionFieldProps {
  sectionId: string;
  initialValue: string | null;
  context: string | null;
  contextOutput: string | null;
  projectId: string;
  templateQueryKey: string[];
}

function useAutoResize(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return ref;
}

function InfoTooltip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-pointer shrink-0" />
      </TooltipTrigger>
      <TooltipContent className="max-w-[220px] text-xs">{text}</TooltipContent>
    </Tooltip>
  );
}

export const DescriptionField = ({
  sectionId,
  initialValue,
  context,
  contextOutput,
  projectId,
  templateQueryKey,
}: DescriptionFieldProps) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();
  const { data: project } = useProject(projectId);
  const model = project?.model ?? "opus-46";

  const [description, setDescription] = useState(initialValue ?? "");
  const [contextValue, setContextValue] = useState(context ?? "");
  const [outputValue, setOutputValue] = useState(contextOutput ?? "");
  const [descCollapsed, setDescCollapsed] = useState(Boolean(contextOutput));
  const [contextCollapsed, setContextCollapsed] = useState(Boolean(contextOutput));
  const [refineLoading, setRefineLoading] = useState(false);
  const [isEditingOutput, setIsEditingOutput] = useState(false);

  useEffect(() => { setDescription(initialValue ?? ""); }, [initialValue]);
  useEffect(() => { setContextValue(context ?? ""); }, [context]);
  useEffect(() => {
    setOutputValue(contextOutput ?? "");
    if (contextOutput) {
      setDescCollapsed(true);
      setContextCollapsed(true);
    }
  }, [contextOutput]);

  const descRef = useAutoResize(description);
  const contextRef = useAutoResize(contextValue);
  const outputRef = useAutoResize(outputValue);

  const updateCache = (fields: Record<string, string | null>) => {
    queryClient.setQueryData<ProjectTemplateData>(templateQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: old.prompt_libraries.map((pl) =>
          pl.id === sectionId ? { ...pl, ...fields } : pl
        ),
      };
    });
  };

  const patch = (fields: Parameters<typeof projectsApi.updateProjectPromptLibrary>[2]) =>
    projectsApi.updateProjectPromptLibrary(projectId, sectionId, fields);

  const handleDescriptionBlur = async () => {
    const trimmed = description.trim();
    if (trimmed === (initialValue ?? "").trim()) return;
    updateCache({ description: trimmed || null });
    try {
      await patch({ description: trimmed || undefined });
    } catch {
      setDescription(initialValue ?? "");
      updateCache({ description: initialValue ?? null });
    }
  };

  const handleContextBlur = async () => {
    const trimmed = contextValue.trim();
    if (trimmed === (context ?? "").trim()) return;
    updateCache({ description_context: trimmed || null });
    try {
      await patch({ description_context: trimmed || undefined });
    } catch {
      setContextValue(context ?? "");
      updateCache({ description_context: context ?? null });
    }
  };

  const handleOutputSave = async () => {
    const trimmed = outputValue.trim();
    setIsEditingOutput(false);
    if (trimmed === (contextOutput ?? "").trim()) return;
    updateCache({ description_ai_output: trimmed || null });
    try {
      await patch({ description_ai_output: trimmed || undefined });
    } catch {
      setOutputValue(contextOutput ?? "");
      updateCache({ description_ai_output: contextOutput ?? null });
    }
  };

  const handleRefine = async () => {
    const descTrimmed = description.trim();
    const ctxTrimmed = contextValue.trim();
    if (refineLoading || (!descTrimmed && !ctxTrimmed)) return;
    setRefineLoading(true);
    setDescCollapsed(true);
    setContextCollapsed(true);
    const previousOutput = contextOutput;
    try {
      const token = await getAccessToken();
      const userMessage = [descTrimmed, ctxTrimmed]
        .filter(Boolean)
        .join("\n\nAdditional context: ");
      const res = await fetch(`${CONTENT_INTELLIGENCE_API_URL}/api/intelligence/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          model,
          priming:
            "You are a medical writing assistant. Using the boilerplate description and any additional context provided, write a detailed, professional section description suitable for a client-facing outline. Return plain text only, no markdown.",
          messages: [{ role: "user", content: userMessage }],
          stream: false,
        }),
      });
      const data = await res.json();
      const assistantMsg = data.messages?.find((m: { role: string }) => m.role === "assistant");
      const newOutput = assistantMsg?.content ?? null;
      if (newOutput) {
        setOutputValue(newOutput);
        setIsEditingOutput(false);
        updateCache({ description_ai_output: newOutput });
        await patch({ description_ai_output: newOutput });
      }
    } catch {
      setOutputValue(previousOutput ?? "");
      updateCache({ description_ai_output: previousOutput ?? null });
    } finally {
      setRefineLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="w-full space-y-2">
        {/* Description */}
        <div>
          {outputValue ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setDescCollapsed((v) => !v)}
            >
              {descCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Description
              <span onClick={(e) => e.stopPropagation()}>
                <InfoTooltip text="What information should this section include or exclude? Provide specific guidance for content creation." />
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
              Description
              <InfoTooltip text="What information should this section include or exclude? Provide specific guidance for content creation." />
            </div>
          )}
          {(!outputValue || !descCollapsed) && (
            <textarea
              ref={descRef}
              rows={1}
              value={description}
              placeholder="Add a description…"
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescriptionBlur}
              className="w-full min-h-[80px] max-h-[400px] text-sm p-3 bg-slate-50 border-none leading-relaxed overflow-y-auto focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-transparent"
            />
          )}
        </div>

        {/* Context */}
        <div>
          {outputValue ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-1"
              onClick={() => setContextCollapsed((v) => !v)}
            >
              {contextCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Context
              <span onClick={(e) => e.stopPropagation()}>
                <InfoTooltip text="Background information and key messages that should inform this section's content." />
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
              Context
              <InfoTooltip text="Background information and key messages that should inform this section's content." />
            </div>
          )}
          {(!outputValue || !contextCollapsed) && (
            <textarea
              ref={contextRef}
              rows={1}
              value={contextValue}
              placeholder="Enter section context…"
              onChange={(e) => setContextValue(e.target.value)}
              onBlur={handleContextBlur}
              className="w-full min-h-[80px] max-h-[400px] text-sm p-3 bg-slate-50 border-none leading-relaxed overflow-y-auto focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-transparent"
            />
          )}
        </div>

        {/* Output (AI-Processed) — only shown when content exists */}
        {outputValue && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
              Output
              <span className="font-normal">(AI-Processed)</span>
              <InfoTooltip text="AI-generated summary based on Description and Context fields. This will be used for client review." />
              <span className="ml-auto">
                {isEditingOutput ? (
                  <button
                    type="button"
                    onClick={handleOutputSave}
                    className="flex items-center gap-1 text-xs text-brand-teal hover:text-brand-teal/80 transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" /> Save
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditingOutput(true)}
                    disabled={refineLoading}
                    className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <SquarePen className="w-3.5 h-3.5" />
                  </button>
                )}
              </span>
            </div>
            <textarea
              ref={outputRef}
              rows={1}
              value={outputValue}
              onChange={(e) => setOutputValue(e.target.value)}
              disabled={!isEditingOutput}
              className="w-full min-h-[80px] max-h-[400px] text-sm p-3 bg-slate-100 border-none leading-relaxed overflow-y-auto focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-transparent disabled:opacity-70 disabled:cursor-default"
            />
          </div>
        )}

        {/* Refine button */}
        <Button
          variant="brand"
          appearance="solid"
          className="w-full"
          onClick={handleRefine}
          disabled={refineLoading || (!description.trim() && !contextValue.trim())}
          type="button"
        >
          {refineLoading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          Refine
        </Button>
      </div>
    </TooltipProvider>
  );
};
